import { animalFilterOptions } from '@/components/Animals/AnimalsTable/AnimalsTable.utils';
import { type AnimalFiltersEntity } from '@/components/Colony/Animals/Animals.utils';
import Loading from '@/components/Loading';
import { type StepFormAction } from '@/components/UI/StepForm/StepForm.model';
import APITable from '@/components/UI/Table/Reusable/APITable';
import {
  type ChangePercentage,
  DEFAULT_CHANGE_PERCENTAGE,
} from '@/components/UI/Table/Reusable/APITable/APITable.model';
import type { TanStackColumnDef, FilterOption } from '@/components/UI/Table/TableComponent.model';
import { _get, _notNil } from '@/littledash';
import { type Animal, type AnimalV1 } from '@/model/Animal.model';
import { type State } from '@/model/State.model';
import { useFetchEntity } from '@/support/Hooks/fetch';
import { type UseFetchEntityOutput } from '@/support/Hooks/fetch/useFetchEntity';
import { RouteService } from '@/support/RouteService';
import { type Dispatch, type FC, useEffect, useMemo, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { createMultiSelectDoubleEncodingProcessor } from '@/utils/TableFilter.utils';
import { type CreateBulkDosingState, dosingAnimalTableColumns } from '../CreateDosing.utils';

const searchByOptions = [
  { value: 'name', display: 'Name' },
  { value: 'tail', display: 'Tail' },
  { value: 'ear', display: 'Ear' },
  { value: 'tag', display: 'Tag' },
  { value: 'donor', display: 'Donor' },
];

interface SelectAnimalsProps {
  state: CreateBulkDosingState;
  dispatch: Dispatch<StepFormAction<CreateBulkDosingState>>;
}

export const SelectAnimals: FC<SelectAnimalsProps> = ({ state, dispatch }) => {
  const [changePercentage, setChangePercentage] = useState<ChangePercentage>(DEFAULT_CHANGE_PERCENTAGE);
  const features = useSelector((state: State) => state.team.features, shallowEqual);
  const { study, treatmentClassification } = state;
  const { study_groups: studyGroups, id: studyId } = study;
  const calculations = study?.settings?.calculations;

  const { entity: animalFilters, entityLoading: animalFiltersLoading }: UseFetchEntityOutput<AnimalFiltersEntity> =
    useFetchEntity({
      entityType: 'animalFilters',
    });

  const { entity: observationOptions }: UseFetchEntityOutput<Array<string>> = useFetchEntity({
    entityType: 'observationFilterOptions',
    params: { id: studyId },
  });

  // Build columns once when dependencies are ready
  const columns = useMemo<Array<TanStackColumnDef<AnimalV1>> | null>(() => {
    if (animalFiltersLoading) {
      return null;
    }
    return dosingAnimalTableColumns(study.id, calculations ?? [], changePercentage, features, animalFilters?.metadata);
  }, [animalFiltersLoading, study.id, calculations, changePercentage, features, animalFilters?.metadata]);

  // Build filter options once when dependencies are ready
  const filterOptions = useMemo<Array<FilterOption>>(() => {
    if (
      _notNil(calculations) &&
      _notNil(studyGroups) &&
      _notNil(studyId) &&
      _notNil(observationOptions) &&
      Array.isArray(animalFilters?.metadata)
    ) {
      return animalFilterOptions(calculations, studyGroups, studyId, observationOptions, animalFilters?.metadata ?? []);
    }
    return [];
  }, [calculations, studyGroups, studyId, observationOptions, animalFilters?.metadata]);

  useEffect(() => {
    dispatch({
      type: 'updateArray',
      id: 'selectedAnimals',
      data: [],
    });
  }, [dispatch]);

  useEffect(() => {
    if (_notNil(columns)) {
      dispatch({ type: 'stepReady' });
    }
  }, [columns, dispatch]);

  const onSelectRow = (rows: Array<any>, state: any) => {
    if ((rows ?? []).length === 0 || (state?.sorting?.length ?? 0) === 0) {
      return;
    }

    const { id = 'id', desc = false } = state.sorting[0];
    let accessor = id;
    // only specify the accessor if the sort is one of the following nested sorts:
    switch (id) {
      case 'cage':
        accessor = 'cage.id';
        break;
      case 'weight':
        accessor = 'latestMeasurement.weight.value';
        break;
      case 'study_group':
        accessor = 'study_group_id';
        break;
    }

    const sortedRows = (rows ?? [])
      .map(({ original }: any) => original)
      .sort((a: Animal, b: Animal) => {
        const sortA = _get(a, accessor);
        const sortB = _get(b, accessor);
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        return desc ? sortB - sortA : sortA - sortB;
      });

    dispatch({
      type: 'updateArray',
      id: 'selectedAnimals',
      data: sortedRows,
    });
  };

  const treatmentIds = state.selectedTreatments.map(({ api_id }) => api_id);

  if (!columns) {
    return (
      <div className="pt4">
        <Loading txt="Loading animals..." />
      </div>
    );
  }

  return (
    <div className="pt4">
      <APITable
        apiInfo={{
          type: 'internalApi',
          route: RouteService.api({
            endpoint: 'GET /api/v1/studies/{studyId}/animals',
            path: { studyId: state.study?.api_id },
            query: {
              perPage: 1000,
              ...(treatmentClassification === 'treatment' && { treatment_ids: treatmentIds }),
            },
          }).url,
        }}
        columns={columns}
        includeParam="latestMeasurement,study_group,cage,metadata"
        defaultSortBy={{ id: 'number', desc: false }}
        searchPlaceholderText="Search"
        searchQueryByOptions={searchByOptions}
        reduxTableName={'dosingSelectAnimals'}
        showMetricChangeFrom={true}
        changePercentage={changePercentage}
        onChangePercentageChange={setChangePercentage}
        onSelectRow={onSelectRow}
        pageSizes={[50, 100, 200, 500, 1000]}
        defaultPageSize={1000}
        filterOptions={filterOptions}
        testIdPrefix="animals"
        loadingClassName="pv3"
        maxHeight={450}
        customFilterProcessor={createMultiSelectDoubleEncodingProcessor(animalFilters?.metadata ?? [])}
      />
    </div>
  );
};
